import * as i0 from '@angular/core';
import { EventEmitter, inject, DestroyRef, Component, Optional, Self, Input, Output, ViewChild, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, merge, map, fromEvent, debounceTime, distinctUntilChanged, scan } from 'rxjs';
import * as i3 from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';

/**
 * This component is used to create a number input field with increment and decrement buttons, can be used in forms
 * @example
 *  <fgbcl-number-input formControlName="quantity"></fgbcl-number-input>
 *  using this as a form control in a form group is not yet tested, but it should work
 */
class NumberInputComponent {
    set minValue(value) {
        this._minValue = value;
        this.minimum.next(value);
    }
    ;
    set maxValue(value) {
        this._maxValue = value;
        this.maximum.next(value);
    }
    set amount(value) {
        this.innerValue = value;
        this.amountSubject.next(value);
    }
    constructor(ngControl) {
        this.ngControl = ngControl;
        this.isActive = true;
        this.limitValueToRange = true;
        this.unlimitedLimit = 100;
        this.amountChange = new EventEmitter();
        this.outOfRange = false;
        this.outOfRangeChange = new EventEmitter();
        this.showErrorMessage = true; // For the sake of backward compatibility added this input, should be removed
        // There is no point showing the error message if the input is changed dynamically to limit the value
        // Suggested to use validator instead to handle the error message(s), it is also hard to maintain the error message(s) in the parent component,
        // It's better to use this as a form control and use validator, it's hard to maintain this as ngModel, two-way binding can cause issues if not handled properly
        this.errorChange = new EventEmitter();
        this.destroyRef = inject(DestroyRef);
        this.increment = new Subject();
        this.decrement = new Subject();
        this.minimum = new Subject();
        this.maximum = new Subject();
        this.amountSubject = new Subject();
        this.showMinMessage = false;
        this.showMaxMessage = false;
        if (this.ngControl)
            this.ngControl.valueAccessor = this;
        // Set private defaults
        this._onChangeCallback = (_) => { };
        this._onTouchedCallback = () => { };
    }
    ngOnInit() {
        if (this.ngControl) {
            this.control = this.ngControl.control;
            this.control.updateValueAndValidity();
        }
        if (!this.innerValue) {
            this.amount = this.minValue;
        }
    }
    ngAfterViewInit() {
        // Merge events
        merge(
        // Input changes
        this.onMinimumChanged.pipe(map(this.minimumHandler)), this.onMaximumChanged.pipe(map(this.maximumHandler)), this.onAmountChanged.pipe(map(this.amountHandler)), 
        // Event changes, button click and input event(s)
        this.onIncrementChanged.pipe(map(this.incrementHandler)), this.onDecrementChanged.pipe(map(this.decrementHandler)), fromEvent(this.inputText.nativeElement, 'input')
            .pipe(map(this.inputHandler), debounceTime(300), distinctUntilChanged())).pipe(
        // Run event function and validate the amount
        scan((state, eventFn) => this.validateAmountHandler(eventFn(state)), { value: this.innerValue, min: this.minValue, max: this.maxValue }), takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            this.writeValue(data.value);
        });
    }
    /** Validate range */
    validateAmountHandler(state) {
        this.setDefaults();
        this.errorChange.emit('');
        // If limit then max is set to 0, set the value to 1
        if (this.limitValueToRange && this.maxValue === 0) {
            return { ...state, value: 1 };
        }
        if (this.limitValueToRange === undefined && state.value > state.max) {
            this.showMaxMessage = true;
            this.errorChange.emit(this.maxMessage);
        }
        if (state.value < state.min) {
            this.showMinMessage = true;
            this.errorChange.emit(this.minMessage);
            return { ...state, value: state.min };
        }
        else if (this.limitValueToRange && state.value > state.max) {
            this.showMaxMessage = true;
            this.errorChange.emit(this.maxMessage);
            return { ...state, value: state.max };
        }
        else if (this.limitValueToRange === false) {
            // There is a max value set for unlimited item, should be handled in the api
            // currently set to 100 as default
            if (state.value > this.unlimitedLimit) {
                return { ...state, value: this.unlimitedLimit };
            }
        }
        return { ...state, value: state.value };
    }
    /**
     * Set defaults to false
     */
    setDefaults() {
        this.showMaxMessage = false;
        this.showMinMessage = false;
    }
    /**
     * Handles the input event
     */
    inputHandler(event) {
        return (state) => ({ ...state, value: +event.target.value || 1 });
    }
    /**
     * Handles the amount event
     */
    amountHandler(event) {
        return (state) => ({ ...state, value: event === 0 ? 1 : event });
    }
    /**
     * Handles increment event, increment by the increment value
     */
    incrementHandler(incrementValue) {
        return (state) => ({ ...state, value: state.value + incrementValue });
    }
    /**
     * Handles decrement event, decrement by the increment value
     */
    decrementHandler(incrementValue) {
        return (state) => ({ ...state, value: state.value - incrementValue });
    }
    /**
     * Maximum @Input event handler
     */
    maximumHandler(max) {
        return (state) => ({ ...state, max });
    }
    /**
     * Minimum @Input event handler
     */
    minimumHandler(min) {
        return (state) => ({ ...state, min });
    }
    get onIncrementChanged() {
        return this.increment.asObservable();
    }
    get onDecrementChanged() {
        return this.decrement.asObservable();
    }
    get onMinimumChanged() {
        return this.minimum.asObservable();
    }
    get onMaximumChanged() {
        return this.maximum.asObservable();
    }
    get onAmountChanged() {
        return this.amountSubject.asObservable();
    }
    get maxValue() {
        return this._maxValue;
    }
    get minValue() {
        return this._minValue;
    }
    writeValue(value) {
        this.innerValue = value;
        this._updateValue();
    }
    /**
     * Update the value of the input
     */
    _updateValue() {
        // Update the amount value
        this._onChangeCallback(this.innerValue);
        // Mark the control as touched
        this._onTouchedCallback();
        // Notify the parent component of the change
        this.amountChange.emit(this.innerValue);
        const isNumberOutOfRange = this.innerValue < this.minValue || this.innerValue > this.maxValue;
        this.outOfRangeChange.emit(isNumberOutOfRange);
    }
    /**
     * Register on change function
     */
    registerOnChange(fn) {
        this._onChangeCallback = fn;
    }
    /**
     * Register on touched function
     */
    registerOnTouched(fn) {
        this._onTouchedCallback = fn;
    }
    /** Prevents using decimal in the number input */
    onNumberKeydown(event) {
        if (event.key === '.')
            event.preventDefault();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NumberInputComponent, deps: [{ token: i1.NgControl, optional: true, self: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.2.1", type: NumberInputComponent, selector: "fgbcl-number-input", inputs: { isActive: "isActive", minMessage: "minMessage", maxMessage: "maxMessage", theme: "theme", incrementValue: "incrementValue", limitValueToRange: "limitValueToRange", unlimitedLimit: "unlimitedLimit", minValue: "minValue", maxValue: "maxValue", amount: "amount", outOfRange: "outOfRange", showErrorMessage: "showErrorMessage" }, outputs: { amountChange: "amountChange", outOfRangeChange: "outOfRangeChange", errorChange: "errorChange" }, viewQueries: [{ propertyName: "inputText", first: true, predicate: ["input"], descendants: true }], ngImport: i0, template: "<div class=\"form-group me-lg-3\">\r\n  <div class=\"quantity-select-container\">\r\n    <div class=\"quantity\">\r\n      <!-- Minus Button -->\r\n      <button\r\n        class=\"btn number-input-decrement-button py-0 px-1\"\r\n        (click)=\"decrement.next(incrementValue)\"\r\n        attr.aria-label=\"{{ 'number.input.decrease.screenreader' | translate }}\"\r\n        [disabled]=\"\r\n          ((limitValueToRange || limitValueToRange === undefined) && innerValue <= minValue) ||\r\n          innerValue <= 1 ||\r\n          !isActive ||\r\n          control?.disabled\r\n        \"\r\n      >\r\n        <span class=\"material-icons material-symbols-outlined d-flex justify-content-center align-items-center\"> remove </span>\r\n      </button>\r\n      <!-- Input Field -->\r\n      <input\r\n        id=\"amountInput\"\r\n        type=\"number\"\r\n        [(ngModel)]=\"innerValue\"\r\n        (keydown)=\"onNumberKeydown($event)\"\r\n        class=\"form-control bid-amount-input px-4 text-center\"\r\n        min=\"{{ minValue }}\"\r\n        max=\"{{ maxValue }}\"\r\n        [disabled]=\"!isActive || control?.disabled || innerValue === 0\"\r\n        #input\r\n      />\r\n      <!-- Plus Button -->\r\n      <button\r\n        class=\"btn number-input-increment-button py-0 px-1\"\r\n        (click)=\"increment.next(incrementValue)\"\r\n        attr.aria-label=\"{{ 'number.input.increase.screenreader' | translate }}\"\r\n        [disabled]=\"\r\n          ((limitValueToRange || limitValueToRange === undefined) && innerValue >= maxValue) || !isActive || control?.disabled\r\n        \"\r\n      >\r\n        <span class=\"material-icons material-symbols-outlined d-flex justify-content-center align-items-center\"> add </span>\r\n      </button>\r\n    </div>\r\n  </div>\r\n</div>\r\n\r\n<!-- Errors-->\r\n<div class=\"errors\" *ngIf=\"showErrorMessage\">\r\n  <p class=\"error\" id=\"minMessage\" *ngIf=\"showMinMessage && minMessage\">* {{ minMessage }}</p>\r\n  <p class=\"error\" id=\"maxMessage\" *ngIf=\"showMaxMessage && maxMessage\">* {{ maxMessage }}</p>\r\n</div>\r\n", styles: [":host{width:100%}input[type=number]{appearance:textfield;-moz-appearance:textfield}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}.quantity-select-container{width:142px}.quantity-select-container .quantity{display:flex;position:relative}.amount-input{border-radius:5px;display:inline;padding:7px 0;background:#f4f4f5;border:0;margin:0!important;text-align:center}.btn,.form-group{margin-bottom:0}.error{font-size:12px;color:red;margin-bottom:0}.form-control{height:34px}.number-input-increment-button,.number-input-decrement-button{position:absolute;font-weight:700;width:34px;height:34px;border-radius:50%;background:#fff}.number-input-increment-button:disabled,.number-input-decrement-button:disabled{background:transparent}.number-input-increment-button{right:4px}.number-input-decrement-button{left:4px}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NumberValueAccessor, selector: "input[type=number][formControlName],input[type=number][formControl],input[type=number][ngModel]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.MinValidator, selector: "input[type=number][min][formControlName],input[type=number][min][formControl],input[type=number][min][ngModel]", inputs: ["min"] }, { kind: "directive", type: i1.MaxValidator, selector: "input[type=number][max][formControlName],input[type=number][max][formControl],input[type=number][max][ngModel]", inputs: ["max"] }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: i3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NumberInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fgbcl-number-input', template: "<div class=\"form-group me-lg-3\">\r\n  <div class=\"quantity-select-container\">\r\n    <div class=\"quantity\">\r\n      <!-- Minus Button -->\r\n      <button\r\n        class=\"btn number-input-decrement-button py-0 px-1\"\r\n        (click)=\"decrement.next(incrementValue)\"\r\n        attr.aria-label=\"{{ 'number.input.decrease.screenreader' | translate }}\"\r\n        [disabled]=\"\r\n          ((limitValueToRange || limitValueToRange === undefined) && innerValue <= minValue) ||\r\n          innerValue <= 1 ||\r\n          !isActive ||\r\n          control?.disabled\r\n        \"\r\n      >\r\n        <span class=\"material-icons material-symbols-outlined d-flex justify-content-center align-items-center\"> remove </span>\r\n      </button>\r\n      <!-- Input Field -->\r\n      <input\r\n        id=\"amountInput\"\r\n        type=\"number\"\r\n        [(ngModel)]=\"innerValue\"\r\n        (keydown)=\"onNumberKeydown($event)\"\r\n        class=\"form-control bid-amount-input px-4 text-center\"\r\n        min=\"{{ minValue }}\"\r\n        max=\"{{ maxValue }}\"\r\n        [disabled]=\"!isActive || control?.disabled || innerValue === 0\"\r\n        #input\r\n      />\r\n      <!-- Plus Button -->\r\n      <button\r\n        class=\"btn number-input-increment-button py-0 px-1\"\r\n        (click)=\"increment.next(incrementValue)\"\r\n        attr.aria-label=\"{{ 'number.input.increase.screenreader' | translate }}\"\r\n        [disabled]=\"\r\n          ((limitValueToRange || limitValueToRange === undefined) && innerValue >= maxValue) || !isActive || control?.disabled\r\n        \"\r\n      >\r\n        <span class=\"material-icons material-symbols-outlined d-flex justify-content-center align-items-center\"> add </span>\r\n      </button>\r\n    </div>\r\n  </div>\r\n</div>\r\n\r\n<!-- Errors-->\r\n<div class=\"errors\" *ngIf=\"showErrorMessage\">\r\n  <p class=\"error\" id=\"minMessage\" *ngIf=\"showMinMessage && minMessage\">* {{ minMessage }}</p>\r\n  <p class=\"error\" id=\"maxMessage\" *ngIf=\"showMaxMessage && maxMessage\">* {{ maxMessage }}</p>\r\n</div>\r\n", styles: [":host{width:100%}input[type=number]{appearance:textfield;-moz-appearance:textfield}input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}.quantity-select-container{width:142px}.quantity-select-container .quantity{display:flex;position:relative}.amount-input{border-radius:5px;display:inline;padding:7px 0;background:#f4f4f5;border:0;margin:0!important;text-align:center}.btn,.form-group{margin-bottom:0}.error{font-size:12px;color:red;margin-bottom:0}.form-control{height:34px}.number-input-increment-button,.number-input-decrement-button{position:absolute;font-weight:700;width:34px;height:34px;border-radius:50%;background:#fff}.number-input-increment-button:disabled,.number-input-decrement-button:disabled{background:transparent}.number-input-increment-button{right:4px}.number-input-decrement-button{left:4px}\n"] }]
        }], ctorParameters: () => [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }], propDecorators: { isActive: [{
                type: Input
            }], minMessage: [{
                type: Input
            }], maxMessage: [{
                type: Input
            }], theme: [{
                type: Input
            }], incrementValue: [{
                type: Input
            }], limitValueToRange: [{
                type: Input
            }], unlimitedLimit: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], amount: [{
                type: Input
            }], amountChange: [{
                type: Output
            }], outOfRange: [{
                type: Input
            }], inputText: [{
                type: ViewChild,
                args: ['input']
            }], outOfRangeChange: [{
                type: Output
            }], showErrorMessage: [{
                type: Input
            }], errorChange: [{
                type: Output
            }] } });

class NumberInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NumberInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.2.1", ngImport: i0, type: NumberInputModule, declarations: [NumberInputComponent], imports: [CommonModule,
            ReactiveFormsModule,
            FormsModule,
            InlineSVGModule,
            FGBSharedModule], exports: [NumberInputComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NumberInputModule, imports: [CommonModule,
            ReactiveFormsModule,
            FormsModule,
            InlineSVGModule,
            FGBSharedModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.2.1", ngImport: i0, type: NumberInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [NumberInputComponent],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        FormsModule,
                        InlineSVGModule,
                        FGBSharedModule
                    ],
                    exports: [NumberInputComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NumberInputComponent, NumberInputModule };
